import { createContext, useContext, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]); // [{ pack_id, title, sub, qty, price, mrp }]
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const addPack = useCallback((pack, { openDrawer = true } = {}) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.pack_id === pack.id);
      if (existing) {
        return prev.map((i) =>
          i.pack_id === pack.id ? { ...i, qty: i.qty + 1 } : i
        );
      }
      return [
        ...prev,
        {
          pack_id: pack.id,
          title: `${pack.title} (${pack.sub})`,
          qty: 1,
          price: pack.price,
          mrp: pack.mrp,
        },
      ];
    });
    if (openDrawer) setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((packId, delta) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.pack_id === packId ? { ...i, qty: Math.max(0, i.qty + delta) } : i
        )
        .filter((i) => i.qty > 0)
    );
  }, []);

  const removeItem = useCallback((packId) => {
    setItems((prev) => prev.filter((i) => i.pack_id !== packId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const { subtotal, mrpTotal, count } = useMemo(() => {
    let subtotal = 0,
      mrpTotal = 0,
      count = 0;
    for (const i of items) {
      subtotal += i.price * i.qty;
      mrpTotal += i.mrp * i.qty;
      count += i.qty;
    }
    return { subtotal, mrpTotal, count };
  }, [items]);

  const value = {
    items,
    addPack,
    updateQty,
    removeItem,
    clearCart,
    subtotal,
    mrpTotal,
    count,
    drawerOpen,
    setDrawerOpen,
    checkoutOpen,
    setCheckoutOpen,
    confirmedOrder,
    setConfirmedOrder,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
